import { useMemo, useSyncExternalStore } from 'react'
import RecentService, { RecentEntry } from '../service/RecentService'
import { EntityKind } from '../routes'
import useRegion from './useRegion'

// Stable references, so useSyncExternalStore doesn't resubscribe on every render
const subscribe = (listener: () => void) => RecentService.subscribe(listener)
const getSnapshot = () => RecentService.snapshot()

// Recently used entities in the current region, newest first
export default function useRecent(kinds?: EntityKind[], limit?: number): RecentEntry[] {
    const entries = useSyncExternalStore(subscribe, getSnapshot)
    const region = useRegion()
    const kindsKey = kinds?.join(',')

    return useMemo(() => entries.filter((entry) => entry.region === region && (!kinds || kinds.includes(entry.kind))).slice(0, limit), [entries, region, kindsKey, limit])
}

export interface RecentOption {
    value: string
    label: string
    // Name of the user or collection account the value belongs to, when known
    name?: string
}

// Only these kinds' labels name the user/account itself (others are references or remarks)
const ownerKinds: EntityKind[] = ['user', 'collectionAccount']

// Unique values of one field across recent entities, used as AutoComplete suggestions
export function useRecentValues(field: 'accountId' | 'accountNumber' | 'userId'): RecentOption[] {
    const entries = useRecent()

    return useMemo(() => {
        const options = new Map<string, RecentOption>()
        entries.forEach((entry) => {
            const value = entry[field]
            if (!value) return
            const name = ownerKinds.includes(entry.kind) ? entry.label : undefined
            const existing = options.get(value)
            // Newest first wins, unless an older entry knows the name
            if (!existing || (!existing.name && name)) options.set(value, { value, label: name ? `${value} · ${name}` : value, name })
        })
        return Array.from(options.values())
    }, [entries, field])
}
